import { supabase } from './supabase';
import type { WithdrawalRequest, RegistryFinancials } from '../types/withdrawal';

export async function getRegistryFinancials(registryId: string): Promise<RegistryFinancials> {
  try {
    // Get total collected amount from both claimed items and group gift contributions
    const { data: collectedData, error: collectedError } = await supabase
      .rpc('get_registry_collected_amount', { registry_id_param: registryId });

    if (collectedError) throw collectedError;

    // Get available balance
    const { data: balanceData, error: balanceError } = await supabase
      .rpc('get_available_balance', { registry_id_param: registryId });

    if (balanceError) throw balanceError;

    // Get withdrawal requests
    const { data: withdrawalData, error: withdrawalError } = await supabase
      .from('withdrawal_requests')
      .select('amount, status')
      .eq('registry_id', registryId);

    if (withdrawalError) throw withdrawalError;

    const totalWithdrawn = withdrawalData
      ?.filter(req => req.status === 'approved' || req.status === 'transferred')
      .reduce((sum, req) => sum + req.amount, 0) || 0;

    const pendingRequests = withdrawalData
      ?.filter(req => req.status === 'pending')
      .reduce((sum, req) => sum + req.amount, 0) || 0;

    return {
      total_collected: collectedData || 0,
      total_withdrawn: totalWithdrawn,
      available_balance: balanceData || 0,
      pending_requests: pendingRequests
    };
  } catch (error) {
    console.error('Error fetching registry financials:', error);
    throw error;
  }
}

export async function createWithdrawalRequest(
  registryId: string,
  amount: number
): Promise<WithdrawalRequest> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) throw new Error('User must be authenticated');

    const { data, error } = await supabase
      .from('withdrawal_requests')
      .insert([{
        registry_id: registryId,
        user_id: user.id,
        amount: amount
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating withdrawal request:', error);
    throw error;
  }
}

export async function getWithdrawalRequests(registryId: string): Promise<WithdrawalRequest[]> {
  try {
    const { data, error } = await supabase
      .from('withdrawal_requests')
      .select('*')
      .eq('registry_id', registryId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching withdrawal requests:', error);
    throw error;
  }
}